import _slicedToArray from "/Users/tannermarshall/Dev/figure53.dev/websites/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/Users/tannermarshall/Dev/figure53.dev/websites/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useRef } from "react";
import { UI, DayFlag, SelectionState } from "./UI.js";
import { DateLib, defaultLocale } from "./classes/DateLib.js";
import { getClassNamesForModifiers } from "./helpers/getClassNamesForModifiers.js";
import { getComponents } from "./helpers/getComponents.js";
import { getDataAttributes } from "./helpers/getDataAttributes.js";
import { getDefaultClassNames } from "./helpers/getDefaultClassNames.js";
import { getFormatters } from "./helpers/getFormatters.js";
import { getMonthOptions } from "./helpers/getMonthOptions.js";
import { getStyleForModifiers } from "./helpers/getStyleForModifiers.js";
import { getWeekdays } from "./helpers/getWeekdays.js";
import { getYearOptions } from "./helpers/getYearOptions.js";
import * as defaultLabels from "./labels/index.js";
import { useAnimation } from "./useAnimation.js";
import { useCalendar } from "./useCalendar.js";
import { dayPickerContext } from "./useDayPicker.js";
import { useFocus } from "./useFocus.js";
import { useGetModifiers } from "./useGetModifiers.js";
import { useSelection } from "./useSelection.js";
import { rangeIncludesDate } from "./utils/rangeIncludesDate.js";
import { isDateRange } from "./utils/typeguards.js";
/**
 * Render the date picker calendar.
 *
 * @group DayPicker
 * @see https://daypicker.dev
 */
export function DayPicker(props) {
  var _useSelection;
  var _useMemo = useMemo(function () {
      var locale = _objectSpread(_objectSpread({}, defaultLocale), props.locale);
      var dateLib = new DateLib({
        locale: locale,
        weekStartsOn: props.broadcastCalendar ? 1 : props.weekStartsOn,
        firstWeekContainsDate: props.firstWeekContainsDate,
        useAdditionalWeekYearTokens: props.useAdditionalWeekYearTokens,
        useAdditionalDayOfYearTokens: props.useAdditionalDayOfYearTokens,
        timeZone: props.timeZone,
        numerals: props.numerals
      }, props.dateLib);
      return {
        dateLib: dateLib,
        components: getComponents(props.components),
        formatters: getFormatters(props.formatters),
        labels: _objectSpread(_objectSpread({}, defaultLabels), props.labels),
        locale: locale,
        classNames: _objectSpread(_objectSpread({}, getDefaultClassNames()), props.classNames)
      };
    }, [props.locale, props.broadcastCalendar, props.weekStartsOn, props.firstWeekContainsDate, props.useAdditionalWeekYearTokens, props.useAdditionalDayOfYearTokens, props.timeZone, props.numerals, props.dateLib, props.components, props.formatters, props.labels, props.classNames]),
    components = _useMemo.components,
    formatters = _useMemo.formatters,
    labels = _useMemo.labels,
    dateLib = _useMemo.dateLib,
    locale = _useMemo.locale,
    classNames = _useMemo.classNames;
  var captionLayout = props.captionLayout,
    mode = props.mode,
    onDayBlur = props.onDayBlur,
    onDayClick = props.onDayClick,
    onDayFocus = props.onDayFocus,
    onDayKeyDown = props.onDayKeyDown,
    onDayMouseEnter = props.onDayMouseEnter,
    onDayMouseLeave = props.onDayMouseLeave,
    onNextClick = props.onNextClick,
    onPrevClick = props.onPrevClick,
    showWeekNumber = props.showWeekNumber,
    styles = props.styles;
  var formatCaption = formatters.formatCaption,
    formatDay = formatters.formatDay,
    formatMonthDropdown = formatters.formatMonthDropdown,
    formatWeekNumber = formatters.formatWeekNumber,
    formatWeekNumberHeader = formatters.formatWeekNumberHeader,
    formatWeekdayName = formatters.formatWeekdayName,
    formatYearDropdown = formatters.formatYearDropdown;
  var calendar = useCalendar(props, dateLib);
  var days = calendar.days,
    months = calendar.months,
    navStart = calendar.navStart,
    navEnd = calendar.navEnd,
    previousMonth = calendar.previousMonth,
    nextMonth = calendar.nextMonth,
    goToMonth = calendar.goToMonth;
  var getModifiers = useGetModifiers(days, props, dateLib);
  var _ref = (_useSelection = useSelection(props, dateLib)) !== null && _useSelection !== void 0 ? _useSelection : {},
    isSelected = _ref.isSelected,
    select = _ref.select,
    selectedValue = _ref.selected;
  var _useFocus = useFocus(props, calendar, getModifiers, isSelected !== null && isSelected !== void 0 ? isSelected : function () {
      return false;
    }, dateLib),
    blur = _useFocus.blur,
    focused = _useFocus.focused,
    isFocusTarget = _useFocus.isFocusTarget,
    moveFocus = _useFocus.moveFocus,
    setFocused = _useFocus.setFocused;
  var labelDayButton = labels.labelDayButton,
    labelGridcell = labels.labelGridcell,
    labelGrid = labels.labelGrid,
    labelMonthDropdown = labels.labelMonthDropdown,
    labelNav = labels.labelNav,
    labelWeekday = labels.labelWeekday,
    labelWeekNumber = labels.labelWeekNumber,
    labelWeekNumberHeader = labels.labelWeekNumberHeader,
    labelYearDropdown = labels.labelYearDropdown;
  var weekdays = useMemo(function () {
    return getWeekdays(dateLib, props.ISOWeek);
  }, [dateLib, props.ISOWeek]);
  var isInteractive = mode !== undefined || onDayClick !== undefined;
  var handlePreviousClick = useCallback(function () {
    if (!previousMonth) return;
    goToMonth(previousMonth);
    onPrevClick === null || onPrevClick === void 0 || onPrevClick(previousMonth);
  }, [previousMonth, goToMonth, onPrevClick]);
  var handleNextClick = useCallback(function () {
    if (!nextMonth) return;
    goToMonth(nextMonth);
    onNextClick === null || onNextClick === void 0 || onNextClick(nextMonth);
  }, [goToMonth, nextMonth, onNextClick]);
  var handleDayClick = useCallback(function (day, m) {
    return function (e) {
      e.preventDefault();
      e.stopPropagation();
      setFocused(day);
      select === null || select === void 0 || select(day.date, m, e);
      onDayClick === null || onDayClick === void 0 || onDayClick(day.date, m, e);
    };
  }, [select, onDayClick, setFocused]);
  var handleDayFocus = useCallback(function (day, m) {
    return function (e) {
      setFocused(day);
      onDayFocus === null || onDayFocus === void 0 || onDayFocus(day.date, m, e);
    };
  }, [onDayFocus, setFocused]);
  var handleDayBlur = useCallback(function (day, m) {
    return function (e) {
      blur();
      onDayBlur === null || onDayBlur === void 0 || onDayBlur(day.date, m, e);
    };
  }, [blur, onDayBlur]);
  var handleDayKeyDown = useCallback(function (day, modifiers) {
    return function (e) {
      var keyMap = {
        ArrowLeft: ["day", props.dir === "rtl" ? "after" : "before"],
        ArrowRight: ["day", props.dir === "rtl" ? "before" : "after"],
        ArrowDown: ["week", "after"],
        ArrowUp: ["week", "before"],
        PageUp: [e.shiftKey ? "year" : "month", "before"],
        PageDown: [e.shiftKey ? "year" : "month", "after"],
        Home: ["startOfWeek", "before"],
        End: ["endOfWeek", "after"]
      };
      if (keyMap[e.key]) {
        e.preventDefault();
        e.stopPropagation();
        var _keyMap$e$key = _slicedToArray(keyMap[e.key], 2),
          moveBy = _keyMap$e$key[0],
          moveDir = _keyMap$e$key[1];
        moveFocus(moveBy, moveDir);
      }
      onDayKeyDown === null || onDayKeyDown === void 0 || onDayKeyDown(day.date, modifiers, e);
    };
  }, [moveFocus, onDayKeyDown, props.dir]);
  var handleDayMouseEnter = useCallback(function (day, modifiers) {
    return function (e) {
      onDayMouseEnter === null || onDayMouseEnter === void 0 || onDayMouseEnter(day.date, modifiers, e);
    };
  }, [onDayMouseEnter]);
  var handleDayMouseLeave = useCallback(function (day, modifiers) {
    return function (e) {
      onDayMouseLeave === null || onDayMouseLeave === void 0 || onDayMouseLeave(day.date, modifiers, e);
    };
  }, [onDayMouseLeave]);
  var handleMonthChange = useCallback(function (date) {
    return function (e) {
      var selectedMonth = Number(e.target.value);
      var month = dateLib.setMonth(dateLib.startOfMonth(date), selectedMonth);
      goToMonth(month);
    };
  }, [dateLib, goToMonth]);
  var handleYearChange = useCallback(function (date) {
    return function (e) {
      var selectedYear = Number(e.target.value);
      var month = dateLib.setYear(dateLib.startOfMonth(date), selectedYear);
      goToMonth(month);
    };
  }, [dateLib, goToMonth]);
  var _useMemo2 = useMemo(function () {
      return {
        className: [classNames[UI.Root], props.className].filter(Boolean).join(" "),
        style: _objectSpread(_objectSpread({}, styles === null || styles === void 0 ? void 0 : styles[UI.Root]), props.style)
      };
    }, [classNames, props.className, props.style, styles]),
    className = _useMemo2.className,
    style = _useMemo2.style;
  var dataAttributes = getDataAttributes(props);
  var rootElRef = useRef(null);
  useAnimation(rootElRef, Boolean(props.animate), {
    classNames: classNames,
    months: months,
    focused: focused,
    dateLib: dateLib
  });
  var contextValue = {
    dayPickerProps: props,
    selected: selectedValue,
    select: select,
    isSelected: isSelected,
    months: months,
    nextMonth: nextMonth,
    previousMonth: previousMonth,
    goToMonth: goToMonth,
    getModifiers: getModifiers,
    components: components,
    classNames: classNames,
    styles: styles,
    labels: labels,
    formatters: formatters
  };
  return React.createElement(dayPickerContext.Provider, {
    value: contextValue
  }, React.createElement(components.Root, _objectSpread({
    rootRef: props.animate ? rootElRef : undefined,
    className: className,
    style: style,
    dir: props.dir,
    id: props.id,
    lang: props.lang,
    nonce: props.nonce,
    title: props.title,
    role: props.role,
    "aria-label": props["aria-label"]
  }, dataAttributes), React.createElement(components.Months, {
    className: classNames[UI.Months],
    style: styles === null || styles === void 0 ? void 0 : styles[UI.Months]
  }, !props.hideNavigation && React.createElement(components.Nav, {
    "data-animated-nav": props.animate ? "true" : undefined,
    className: classNames[UI.Nav],
    style: styles === null || styles === void 0 ? void 0 : styles[UI.Nav],
    "aria-label": labelNav(),
    onPreviousClick: handlePreviousClick,
    onNextClick: handleNextClick,
    previousMonth: previousMonth,
    nextMonth: nextMonth
  }), months.map(function (calendarMonth, displayIndex) {
    var dropdownMonths = getMonthOptions(calendarMonth.date, navStart, navEnd, formatters, dateLib);
    var dropdownYears = getYearOptions(navStart, navEnd, formatters, dateLib);
    return React.createElement(components.Month, {
      "data-animated-month": props.animate ? "true" : undefined,
      className: classNames[UI.Month],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Month],
      key: displayIndex,
      displayIndex: displayIndex,
      calendarMonth: calendarMonth
    }, React.createElement(components.MonthCaption, {
      "data-animated-caption": props.animate ? "true" : undefined,
      className: classNames[UI.MonthCaption],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.MonthCaption],
      calendarMonth: calendarMonth,
      displayIndex: displayIndex
    }, captionLayout !== null && captionLayout !== void 0 && captionLayout.startsWith("dropdown") ? React.createElement(components.DropdownNav, {
      className: classNames[UI.Dropdowns],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Dropdowns]
    }, captionLayout === "dropdown" || captionLayout === "dropdown-months" ? React.createElement(components.MonthsDropdown, {
      className: classNames[UI.MonthsDropdown],
      "aria-label": labelMonthDropdown(),
      classNames: classNames,
      components: components,
      disabled: Boolean(props.disableNavigation),
      onChange: handleMonthChange(calendarMonth.date),
      options: dropdownMonths,
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Dropdown],
      value: dateLib.getMonth(calendarMonth.date)
    }) : React.createElement("span", null, formatMonthDropdown(calendarMonth.date, dateLib)), captionLayout === "dropdown" || captionLayout === "dropdown-years" ? React.createElement(components.YearsDropdown, {
      className: classNames[UI.YearsDropdown],
      "aria-label": labelYearDropdown(dateLib.options),
      classNames: classNames,
      components: components,
      disabled: Boolean(props.disableNavigation),
      onChange: handleYearChange(calendarMonth.date),
      options: dropdownYears,
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Dropdown],
      value: dateLib.getYear(calendarMonth.date)
    }) : React.createElement("span", null, formatYearDropdown(calendarMonth.date, dateLib)), React.createElement("span", {
      role: "status",
      "aria-live": "polite",
      style: {
        border: 0,
        clip: "rect(0 0 0 0)",
        height: "1px",
        margin: "-1px",
        overflow: "hidden",
        padding: 0,
        position: "absolute",
        width: "1px",
        whiteSpace: "nowrap",
        wordWrap: "normal"
      }
    }, formatCaption(calendarMonth.date, dateLib.options, dateLib))) : React.createElement(components.CaptionLabel, {
      className: classNames[UI.CaptionLabel],
      role: "status",
      "aria-live": "polite"
    }, formatCaption(calendarMonth.date, dateLib.options, dateLib))), React.createElement(components.MonthGrid, {
      role: "grid",
      "aria-multiselectable": mode === "multiple" || mode === "range",
      "aria-label": labelGrid(calendarMonth.date, dateLib.options, dateLib) || undefined,
      className: classNames[UI.MonthGrid],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.MonthGrid]
    }, !props.hideWeekdays && React.createElement(components.Weekdays, {
      "data-animated-weekdays": props.animate ? "true" : undefined,
      className: classNames[UI.Weekdays],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Weekdays]
    }, showWeekNumber && React.createElement(components.WeekNumberHeader, {
      "aria-label": labelWeekNumberHeader(dateLib.options),
      className: classNames[UI.WeekNumberHeader],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.WeekNumberHeader],
      scope: "col"
    }, formatWeekNumberHeader()), weekdays.map(function (weekday, i) {
      return React.createElement(components.Weekday, {
        "aria-label": labelWeekday(weekday, dateLib.options, dateLib),
        className: classNames[UI.Weekday],
        key: i,
        style: styles === null || styles === void 0 ? void 0 : styles[UI.Weekday],
        scope: "col"
      }, formatWeekdayName(weekday, dateLib.options, dateLib));
    })), React.createElement(components.Weeks, {
      "data-animated-weeks": props.animate ? "true" : undefined,
      className: classNames[UI.Weeks],
      style: styles === null || styles === void 0 ? void 0 : styles[UI.Weeks]
    }, calendarMonth.weeks.map(function (week, weekIndex) {
      return React.createElement(components.Week, {
        className: classNames[UI.Week],
        key: week.weekNumber,
        style: styles === null || styles === void 0 ? void 0 : styles[UI.Week],
        week: week
      }, showWeekNumber && React.createElement(components.WeekNumber, {
        week: week,
        style: styles === null || styles === void 0 ? void 0 : styles[UI.WeekNumber],
        "aria-label": labelWeekNumber(week.weekNumber, {
          locale: locale
        }),
        className: classNames[UI.WeekNumber],
        scope: "row",
        role: "rowheader"
      }, formatWeekNumber(week.weekNumber)), week.days.map(function (day) {
        var date = day.date;
        var modifiers = getModifiers(day);
        modifiers[DayFlag.focused] = !modifiers.hidden && Boolean(focused === null || focused === void 0 ? void 0 : focused.isEqualTo(day));
        modifiers[SelectionState.selected] = (isSelected === null || isSelected === void 0 ? void 0 : isSelected(date)) || modifiers.selected;
        if (isDateRange(selectedValue)) {
          // add range modifiers
          var from = selectedValue.from,
            to = selectedValue.to;
          modifiers[SelectionState.range_start] = Boolean(from && to && dateLib.isSameDay(date, from));
          modifiers[SelectionState.range_end] = Boolean(from && to && dateLib.isSameDay(date, to));
          modifiers[SelectionState.range_middle] = rangeIncludesDate(selectedValue, date, true, dateLib);
        }
        var style = getStyleForModifiers(modifiers, styles, props.modifiersStyles);
        var className = getClassNamesForModifiers(modifiers, classNames, props.modifiersClassNames);
        var ariaLabel = !isInteractive && !modifiers.hidden ? labelGridcell(date, modifiers, dateLib.options, dateLib) : undefined;
        return React.createElement(components.Day, {
          key: "".concat(dateLib.format(date, "yyyy-MM-dd"), "_").concat(dateLib.format(day.displayMonth, "yyyy-MM")),
          day: day,
          modifiers: modifiers,
          className: className.join(" "),
          style: style,
          role: "gridcell",
          "aria-selected": modifiers.selected || undefined,
          "aria-label": ariaLabel,
          "data-day": dateLib.format(date, "yyyy-MM-dd"),
          "data-month": day.outside ? dateLib.format(date, "yyyy-MM") : undefined,
          "data-selected": modifiers.selected || undefined,
          "data-disabled": modifiers.disabled || undefined,
          "data-hidden": modifiers.hidden || undefined,
          "data-outside": day.outside || undefined,
          "data-focused": modifiers.focused || undefined,
          "data-today": modifiers.today || undefined
        }, !modifiers.hidden && isInteractive ? React.createElement(components.DayButton, {
          className: classNames[UI.DayButton],
          style: styles === null || styles === void 0 ? void 0 : styles[UI.DayButton],
          type: "button",
          day: day,
          modifiers: modifiers,
          disabled: modifiers.disabled || undefined,
          tabIndex: isFocusTarget(day) ? 0 : -1,
          "aria-label": labelDayButton(date, modifiers, dateLib.options, dateLib),
          onClick: handleDayClick(day, modifiers),
          onBlur: handleDayBlur(day, modifiers),
          onFocus: handleDayFocus(day, modifiers),
          onKeyDown: handleDayKeyDown(day, modifiers),
          onMouseEnter: handleDayMouseEnter(day, modifiers),
          onMouseLeave: handleDayMouseLeave(day, modifiers)
        }, formatDay(date, dateLib.options, dateLib)) : !modifiers.hidden && formatDay(day.date, dateLib.options, dateLib));
      }));
    }))));
  })), props.footer && React.createElement(components.Footer, {
    className: classNames[UI.Footer],
    style: styles === null || styles === void 0 ? void 0 : styles[UI.Footer],
    role: "status",
    "aria-live": "polite"
  }, props.footer)));
}